"use client";
import { CheckIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import React, { createContext, PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

type SingleSelectInputBoxProps<T> = {
  value: T | null | undefined;
  onChange: (selected: T | null) => void;
  isMultiSelect: false;
};

type MultiSelectInputBoxProps<T> = {
  value: T[];
  onChange: (selected: T[]) => void;
  isMultiSelect: true;
};

export type SelectInputBoxProps<T> = {
  className?: string;
  error?: string;
} & (SingleSelectInputBoxProps<T> | MultiSelectInputBoxProps<T>);

type SelectBoxContextType<T> = {
  selected: T | T[];
  toggleSelected: (value: T) => void;
};

const SelectBoxContext = createContext<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selected: any | any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleSelected: (value: any) => void;
}>({
  selected: [],
  toggleSelected: () => {
    throw new Error("SelectBoxContext not initialized");
  },
});

export const SelectInputBox = <T,>({
  children,
  value: selected,
  onChange,
  className,
  isMultiSelect,
  error,
}: PropsWithChildren<SelectInputBoxProps<T>>) => {
  const toggleSelected = (value: T) => {
    if (!isMultiSelect) {
      onChange(value === selected ? null : value);
      return;
    }

    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <SelectBoxContext.Provider value={{ selected, toggleSelected }}>
      <div className="max-w-full">
        <div
          className={classNames(
            className,
            "flex flex-col overflow-y-scroll w-full border  rounded-lg divide-y divide-gray-200",
            {
              "border-gray-400": !error,
              "border-red-600": error,
            },
          )}
        >
          {children}
        </div>
        {typeof error === "string" && (
          <div className="ml-1 mt-1 text-sm text-error">{error}</div>
        )}
      </div>
    </SelectBoxContext.Provider>
  );
};

type SelectInputBoxType<T> = {
  value: T;
};

export const SelectInputBoxOption = <T,>({
  children,
  value,
}: PropsWithChildren<SelectInputBoxType<T>>) => {
  const { selected, toggleSelected } = React.useContext(
    SelectBoxContext,
  ) as SelectBoxContextType<T>;

  const isSelected = Array.isArray(selected)
    ? selected.includes(value)
    : selected === value;

  return (
    <div
      className={twMerge(
        "w-full py-2 px-2 text-gray-900 bg-white",
        isSelected && "bg-primary hover:bg-primaryHover text-primary-contrast",
        !isSelected && "bg:white hover:bg-gray-100",
      )}
      onClick={() => {
        toggleSelected(value);
      }}
    >
      <div className="flex flex-row justify-between cursor-pointer text-base px-1">
        <div className="flex flex-row items-center divide-y divide-gray-300">
          {children}
        </div>
        {isSelected && <CheckIcon width={20} height={20} />}
      </div>
    </div>
  );
};

export const SelectInputBoxIcon: React.FC<PropsWithChildren> = ({
  children,
}) => {
  return <div className="mr-4">{children}</div>;
};
